import { useState, useCallback } from 'react';
import { Loader2, Copy, Check, Clock, Zap, IterationCw, RotateCw, Info } from 'lucide-react';
import Badge, { getStatusVariant } from '@/components/ui/Badge';
import { MarkdownContent } from '@/components/ui/markdown-content';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { formatRelativeTime } from '../activity/utils';
import { formatElapsed } from '../../utils/format';
import { spaceApi } from '../../services/api';
import type { RunSummary } from '../../types/runs';

/**
 * Reduce a markdown response to a compact, single-line-flow preview: drop
 * block-level structure (headings, lists, code blocks, tables, blockquotes,
 * rules, images) but keep simple inline formatting (bold, italic, links, inline
 * code) so the runs list shows clean prose, not a rendered document.
 */
export function toInlinePreview(md: string): string {
  return md
    .slice(0, 600) // bound the work; the visible preview is far shorter
    .replace(/```[\s\S]*?```/g, ' ') // fenced code blocks
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '') // images
    .replace(/^[ \t]*#{1,6}[ \t]+/gm, '') // heading markers (keep the text)
    .replace(/^[ \t]*>+[ \t]?/gm, '') // blockquote markers
    .replace(/^[ \t]*(?:[-*+]|\d+[.)])[ \t]+/gm, '') // list markers
    .replace(/^[ \t]*([-*_])(?:[ \t]*\1){2,}[ \t]*$/gm, ' ') // horizontal rules
    .replace(/^[ \t]*\|?[ \t:|-]+\|?[ \t]*$/gm, ' ') // table separator rows
    .replace(/\|/g, ' ') // table cell pipes
    .replace(/\s+/g, ' ') // collapse whitespace/newlines
    .trim();
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="opacity-0 group-hover/row:opacity-100 transition-opacity p-0.5 rounded text-muted-foreground hover:text-foreground"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      title="Copy"
    >
      {copied ? <Check className="h-2.5 w-2.5" /> : <Copy className="h-2.5 w-2.5" />}
    </button>
  );
}

function DetailRow({ label, value, children }: { label: string; value: string; children: React.ReactNode }) {
  return (
    <div className="group/row contents">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono">{children}</dd>
      <dd><CopyButton value={value} /></dd>
    </div>
  );
}

interface RunCardProps {
  run: RunSummary;
  isRealtime: boolean;
  isSelected?: boolean;
  // Owning space for this run, resolved from agentId via /me/agents.
  // Required for retry — the action targets the space-scoped endpoint.
  runSpaceId?: string;
  onClick: () => void;
}

export default function RunCard({ run, isRealtime, isSelected, runSpaceId, onClick }: RunCardProps) {
  const [copied, setCopied] = useState(false);
  const [retrying, setRetrying] = useState(false);

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(run, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [run]);

  const handleRetry = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (retrying || !runSpaceId) return;
    setRetrying(true);
    spaceApi(runSpaceId).runs.retry(run.runId)
      .catch(() => {})
      .finally(() => setRetrying(false));
  }, [run.runId, retrying, runSpaceId]);

  const isActive = run.status === 'running' || run.status === 'pending';
  const canRetry =
    !!runSpaceId &&
    (run.status === 'failed' || run.status === 'error' || run.status === 'cancelled');
  const durationSec = run.durationMs ? Math.round(run.durationMs / 1000) : undefined;

  return (
    <div
      className={`group relative rounded-lg border p-4 cursor-pointer transition-colors ${
        isSelected
          ? 'bg-primary-50 dark:bg-primary-900/30 ring-1 ring-primary-200 dark:ring-primary-800'
          : 'bg-card hover:bg-accent/50'
      }`}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      {/* Top row */}
      <div className="flex items-center gap-2 mb-2">
        {isRealtime && (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500" />
          </span>
        )}
        {isActive && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
        {run.agentName && (
          <span className="text-sm font-medium text-foreground truncate">{run.agentName}</span>
        )}
        <Badge variant={getStatusVariant(run.status)}>{run.status}</Badge>
        <span className="text-xs text-muted-foreground ml-auto shrink-0">
          {formatRelativeTime(run.createdAt)}
        </span>
        {canRetry && (
          <button
            type="button"
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-muted-foreground hover:text-foreground disabled:opacity-50"
            onClick={handleRetry}
            disabled={retrying}
            title="Retry run"
          >
            <RotateCw className={`h-3.5 w-3.5 ${retrying ? 'animate-spin' : ''}`} />
          </button>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-muted-foreground hover:text-foreground"
              onClick={(e) => e.stopPropagation()}
              title="Run details"
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-auto max-w-lg p-3" onClick={(e) => e.stopPropagation()}>
            <dl className="grid grid-cols-[auto_1fr_auto] gap-x-3 gap-y-1.5 text-xs whitespace-nowrap items-center">
              <DetailRow label="Created" value={run.createdAt}>{new Date(run.createdAt).toLocaleString()} ({formatRelativeTime(run.createdAt)})</DetailRow>
              <DetailRow label="Updated" value={run.updatedAt}>{new Date(run.updatedAt).toLocaleString()} ({formatRelativeTime(run.updatedAt)})</DetailRow>
              <DetailRow label="Run ID" value={run.runId}>{run.runId}</DetailRow>
              <DetailRow label="Agent ID" value={run.agentId}>{run.agentId}</DetailRow>
              {run.conversationId && (
                <DetailRow label="Conversation" value={run.conversationId}>{run.conversationId}</DetailRow>
              )}
              {run.executionMode && (
                <DetailRow label="Execution" value={run.executionMode}>{run.executionMode}</DetailRow>
              )}
              {run.parentRunId && (
                <DetailRow label="Parent Run" value={run.parentRunId}>{run.parentRunId}</DetailRow>
              )}
            </dl>
          </PopoverContent>
        </Popover>
        <button
          type="button"
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-muted-foreground hover:text-foreground"
          onClick={handleCopy}
          title="Copy run JSON"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Middle: response preview or tool summary */}
      <div className="text-xs text-muted-foreground mb-2 min-h-[1.25rem]">
        {run.responsePreview ? (
          <div className="line-clamp-2 leading-snug">
            <MarkdownContent className="[&_p]:!my-0 [&_p]:inline [&_*]:!text-xs [&_code]:!text-[0.7rem] [&_h1]:!font-semibold [&_h2]:!font-semibold [&_h3]:!font-semibold">
              {toInlinePreview(run.responsePreview).slice(0, 240)}
            </MarkdownContent>
            {run.isStreaming && (
              <span className="inline-block w-1.5 h-3 bg-foreground/60 animate-pulse ml-0.5 align-text-bottom" />
            )}
          </div>
        ) : run.error ? (
          <p className="line-clamp-2 text-red-500">{run.error}</p>
        ) : run.toolCallCount > 0 ? (
          <p className="line-clamp-1">
            Used {run.toolCallCount} tool{run.toolCallCount !== 1 ? 's' : ''}
            {run.toolCallNames.length > 0 && `: ${run.toolCallNames.join(', ')}`}
          </p>
        ) : isActive ? (
          <p className="italic">Running...</p>
        ) : null}
      </div>

      {/* Bottom row: metadata */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {run.usage && (
          <span className="flex items-center gap-1">
            <Zap className="h-3 w-3" />
            {run.usage.totalTokens.toLocaleString()} tokens
          </span>
        )}
        {durationSec !== undefined && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatElapsed(durationSec)}
          </span>
        )}
        {run.steps && run.steps.length > 0 && (
          <span className="text-muted-foreground/60">
            {run.steps.length} step{run.steps.length !== 1 ? 's' : ''}
          </span>
        )}
        {run.currentIteration !== undefined && run.currentIteration > 0 && (
          <span className="flex items-center gap-1">
            <IterationCw className="h-3 w-3" />
            {run.currentIteration} iteration{run.currentIteration !== 1 ? 's' : ''}
          </span>
        )}
        <span className="ml-auto font-mono text-[10px] text-muted-foreground/50">{run.runId}</span>
      </div>
    </div>
  );
}
